import React, { ComponentProps, forwardRef, useMemo } from 'react'

import ExternalLinks from 'ui/components/ExternalLinks'
import { useData } from 'ui/contexts/InjectionContext'

export type TSocialMedia =
  | 'Bilibili'
  | 'BlueSky'
  | 'Discord'
  | 'Facebook'
  | 'GitHub'
  | 'Instagram'
  | 'LinkedIn'
  | 'Reddit'
  | 'Steam'
  | 'Substack'
  | 'TikTok'
  | 'X'
  | 'YouTube'

interface Props {
  links: {
    ariaLabel: TSocialMedia
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
    to: string
  }[]
}

const SocialMedia = forwardRef<
  HTMLAnchorElement,
  Omit<ComponentProps<typeof ExternalLinks>, 'links'> & Props
>(({ links, ...props }, ref) => {
  const {
    bilibili,
    bluesky,
    discord,
    facebook,
    github,
    instagram,
    linkedin,
    reddit,
    steam,
    substack,
    tiktok,
    x,
    youtube,
  } = useData()

  const bilibiliIcon = bilibili?.publicURL
  const blueskyIcon = bluesky?.publicURL
  const discordIcon = discord?.publicURL
  const facebookIcon = facebook?.publicURL
  const githubIcon = github?.publicURL
  const instagramIcon = instagram?.publicURL
  const linkedinIcon = linkedin?.publicURL
  const redditIcon = reddit?.publicURL
  const steamIcon = steam?.publicURL
  const substackIcon = substack?.publicURL
  const tiktokIcon = tiktok?.publicURL
  const xIcon = x?.publicURL
  const youtubeIcon = youtube?.publicURL

  const mappedLinks = useMemo(
    () =>
      links.map(link => {
        let src

        switch (link.ariaLabel) {
          case 'Bilibili':
            src = bilibiliIcon
            break
          case 'BlueSky':
            src = blueskyIcon
            break
          case 'Discord':
            src = discordIcon
            break
          case 'Facebook':
            src = facebookIcon
            break
          case 'GitHub':
            src = githubIcon
            break
          case 'Instagram':
            src = instagramIcon
            break
          case 'LinkedIn':
            src = linkedinIcon
            break
          case 'Reddit':
            src = redditIcon
            break
          case 'Steam':
            src = steamIcon
            break
          case 'Substack':
            src = substackIcon
            break
          case 'TikTok':
            src = tiktokIcon
            break
          case 'X':
            src = xIcon
            break
          case 'YouTube':
            src = youtubeIcon
            break
          default:
            throw new Error('Unsupported social media type!')
        }

        return { ...link, src }
      }),
    [
      bilibiliIcon,
      blueskyIcon,
      discordIcon,
      facebookIcon,
      githubIcon,
      instagramIcon,
      linkedinIcon,
      links,
      redditIcon,
      steamIcon,
      substackIcon,
      tiktokIcon,
      xIcon,
      youtubeIcon,
    ]
  )

  return <ExternalLinks ref={ref} links={mappedLinks} {...props} />
})

export default SocialMedia
